<template>
    <div class="templates-selection-stage">
        <header class="template-gallery-header">
            <h4 class="stage-title">
                {{ $t('unlayerBuilder.templateSelection.selectATemplate') }}
            </h4>

            <DsTabHeader>
                <li
                    data-qa="template-selection-gallery-tab-header"
                    class="tab"
                    :class="{ active: isGallery }"
                    @click="selectGallery"
                >
                    {{ $t('unlayerBuilder.templateSelection.tabs.gallery') }}
                </li>

                <li
                    data-qa="template-selection-saved-tab-header"
                    class="tab"
                    :class="{ active: isSaved }"
                    @click="selectSaved"
                >
                    {{ $t('unlayerBuilder.templateSelection.tabs.saved') }}
                </li>

                <template v-if="isBroadcast">
                    <li
                        data-qa="template-selection-sent-tab-header"
                        class="tab"
                        :class="{ active: isSent }"
                        @click="selectSent"
                    >
                        {{ $t('unlayerBuilder.templateSelection.tabs.sent') }}
                    </li>

                    <li
                        data-qa="template-selection-drafts-tab-header"
                        class="tab"
                        :class="{ active: isDrafts }"
                        @click="selectDrafts"
                    >
                        {{ $t('unlayerBuilder.templateSelection.tabs.drafts') }}
                    </li>
                </template>
            </DsTabHeader>
        </header>

        <TemplateSelectionGalleryPanel
            v-if="isGallery"
            class="selection-panel"
            :marketing-email-id="marketingEmailId"
            :preview-enabled="previewEnabled"
            :source="source"
            @attempt-to-select-template="(payload) => handleAttemptToSelectNewTemplate(payload)"
            @template-created="$emit('template-created', $event)"
            @template-selected="$emit('template-selected', $event)"
        />

        <TemplateSelectionSavedPanel
            v-if="isSaved"
            class="selection-panel"
            :marketing-email-id="marketingEmailId"
            :preview-enabled="previewEnabled"
            :preview-logo-branding-center-enabled="previewLogoBrandingCenterEnabled"
            :filter-my-templates-enabled="filterMyTemplatesEnabled"
            :template-search-enabled="templateSearchEnabled"
            :is-max-classic="isMaxClassic"
            :source="source"
            :template-id="externalTemplateId"
            :unlayer-email-logo-tool-enabled="unlayerEmailLogoToolEnabled"
            @goToGallery="selectGallery"
            @attempt-to-select-template="(payload) => handleAttemptToSelectNewTemplate(payload)"
            @template-created="$emit('template-created', $event)"
            @template-selected="$emit('template-selected', $event)"
            @create-saved-template="(payload) => $emit('create-saved-template', payload)"
        />

        <TemplateSelectionSentPanel
            v-if="isSent"
            class="selection-panel"
            :preview-enabled="previewEnabled"
            :source="source"
            @goToGallery="selectGallery"
            @attempt-to-select-template="(payload) => handleAttemptToSelectNewTemplate(payload)"
            @template-created="$emit('template-created', $event)"
            @template-selected="$emit('template-selected', $event)"
        />

        <TemplateSelectionDraftsPanel
            v-if="isDrafts"
            class="selection-panel"
            :current-template-id="currentTemplateId"
            :preview-enabled="previewEnabled"
            :source="source"
            @edit-broadcast="$emit('edit-broadcast', $event)"
            @goToGallery="selectGallery"
            @attempt-to-select-template="(payload) => handleAttemptToSelectNewTemplate(payload)"
            @template-created="$emit('template-created', $event)"
            @template-selected="$emit('template-selected', $event)"
        />

        <DsConfirmDialog
            v-if="isChangeTemplateModalOpen"
            :is-open="isChangeTemplateModalOpen"
            destructive
            class="change-template-confirm-dialog"
            :title="$t('unlayerBuilder.changeTemplate.title')"
            :message="$t('unlayerBuilder.changeTemplate.description')"
            :confirm-label="$t('unlayerBuilder.changeTemplate.replaceButton')"
            :cancel-label="$t('unlayerBuilder.changeTemplate.cancelButton')"
            @confirm="handleSelectNewTemplate"
            @cancel="isChangeTemplateModalOpen=false"
        />
    </div>
</template>

<script>
import TemplateSelectionDraftsPanel from './TemplateSelectionDraftsPanel';
import TemplateSelectionGalleryPanel from './TemplateSelectionGalleryPanel';
import TemplateSelectionSavedPanel from './TemplateSelectionSavedPanel';
import TemplateSelectionSentPanel from './TemplateSelectionSentPanel';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';

const TABS = Object.freeze({
    GALLERY: 'gallery',
    SAVED: 'saved',
    SENT: 'sent',
    DRAFTS: 'drafts',
});

export default {
    components: {
        TemplateSelectionDraftsPanel,
        TemplateSelectionGalleryPanel,
        TemplateSelectionSavedPanel,
        TemplateSelectionSentPanel,
    },

    props: {
        templateId: {
            type: [String, Number],
            required: false,
        },
        marketingEmailId: String,
        previewEnabled: Boolean,
        previewLogoBrandingCenterEnabled: Boolean,
        filterMyTemplatesEnabled: Boolean,
        templateSearchEnabled: Boolean,
        isMaxClassic: Boolean,
        templateHtmlExists: Boolean,
        source: {
            type: String,
            default: SOURCES.CAMPAIGN,
        },
        externalTemplateId: String,
        unlayerEmailLogoToolEnabled: Boolean,
    },

    data() {
        return {
            tab: TABS.GALLERY,
            isChangeTemplateModalOpen: false,
            changeTemplatePayload: {
                callback: () => {},
                type: '',
                template: {},
            },
        };
    },

    computed: {
        isGallery() {
            return this.tab === TABS.GALLERY;
        },

        isSaved() {
            return this.tab === TABS.SAVED;
        },

        isSent() {
            return this.tab === TABS.SENT;
        },

        isDrafts() {
            return this.tab === TABS.DRAFTS;
        },

        isBroadcast() {
            return this.source === SOURCES.BROADCAST;
        },

        currentTemplateId() {
            return this.templateId ?? null;
        },
    },

    methods: {
        selectGallery() {
            this.tab = TABS.GALLERY;
        },

        selectSaved() {
            this.tab = TABS.SAVED;
        },

        selectSent() {
            this.tab = TABS.SENT;
        },

        selectDrafts() {
            this.tab = TABS.DRAFTS;
        },

        handleAttemptToSelectNewTemplate(payload) {
            this.changeTemplatePayload = payload;

            if (!this.templateHtmlExists) {
                this.handleSelectNewTemplate();

                return;
            }

            this.isChangeTemplateModalOpen = true;
        },

        handleSelectNewTemplate() {
            const { callback, type, template } = this.changeTemplatePayload;

            this.isChangeTemplateModalOpen = false;

            if (type === 'scratch' || type === 'simple-text') {
                callback();
            } else if (type === 'public' || type === 'saved' || type === 'draftOrSent') {
                callback(template);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.templates-selection-stage {
    height: 100%;
    background: $color-gray-050;
    display: flex;
    flex-direction: column;
}

.template-gallery-header {
    border-bottom: 1px solid $color-gray-200;
    background: $color-paper;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 5;
    padding: 0 $spacing-200;
    flex: 0;
}

.selection-panel {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.stage-title {
    font-weight: 700;
    line-height: $line-height-lg;
    text-align: center;
    padding-top: $spacing-300;
    padding-bottom: $spacing-200;
}

.change-template-confirm-dialog :deep(.modal-content-wrapper) {
    border-radius: $border-radius;
}
</style>
